import {formatInvoiceCount} from '../../../../modules/apps/invoice-management/invoices-list/core/_formatters'
import {getInvoiceEntrySourceInfo} from '../../../../modules/apps/invoice-management/invoices-list/core/invoiceEntrySourceHelpers'
import {
  buildInvoiceListFilterUrl,
  buildInvoiceListTargetUrl,
} from '../../../../modules/apps/invoice-management/invoices-list/core/invoiceListTarget'
import {
  DashboardActivityBatch,
  DashboardLiveOperation,
  DashboardProduct,
  DashboardStatusCounts,
} from './_models'

export type DashboardStatusKey = keyof DashboardStatusCounts

export type DashboardStatusSegment = {
  key: DashboardStatusKey
  // The invoice list's send-status filter value.
  listKey: string
  label: string
  tone: 'success' | 'danger' | 'warning' | 'info' | 'muted' | 'slate'
  icon: string
  count: number
}

// The labels and order of the status cards above the invoice list, so a batch reads the same
// as the list it opens.
const STATUS_ORDER: Array<Omit<DashboardStatusSegment, 'count'>> = [
  {
    key: 'success',
    listKey: 'success',
    label: 'ارسال موفق',
    tone: 'success',
    icon: 'la-check-circle',
  },
  {
    key: 'errored',
    listKey: 'errored',
    label: 'دارای خطا',
    tone: 'danger',
    icon: 'la-exclamation-triangle',
  },
  {
    key: 'pending',
    listKey: 'pending',
    label: 'در صف ارسال یا استعلام',
    tone: 'warning',
    icon: 'la-clock',
  },
  {
    key: 'unknown',
    listKey: 'unknown',
    label: 'استعلام نامشخص',
    tone: 'info',
    icon: 'la-question-circle',
  },
  {key: 'notSent', listKey: 'notsend', label: 'ارسال‌نشده', tone: 'muted', icon: 'la-inbox'},
  {key: 'canceled', listKey: 'canceled', label: 'ابطال‌شده', tone: 'slate', icon: 'la-ban'},
]

export const getStatusSegments = (statuses?: DashboardStatusCounts): DashboardStatusSegment[] =>
  STATUS_ORDER.map((status) => ({...status, count: Number(statuses?.[status.key] || 0)})).filter(
    (status) => status.count > 0
  )

// "Ten minutes ago" in the panel's wording; whole units only, never "0 دقیقه".
export const formatAge = (seconds?: number | null): string => {
  const value = Math.max(0, Math.floor(Number(seconds || 0)))
  if (value < 60) return 'همین حالا'
  const minutes = Math.floor(value / 60)
  if (minutes < 60) return `${formatInvoiceCount(minutes)} دقیقه پیش`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${formatInvoiceCount(hours)} ساعت پیش`
  const days = Math.floor(hours / 24)
  return `${formatInvoiceCount(days)} روز پیش`
}

export const formatDayLabel = (day: string, today?: string, yesterday?: string): string => {
  if (day && day === today) return 'امروز'
  if (day && day === yesterday) return 'دیروز'
  return day
}

export const formatTimeRange = (fromTime: string, toTime: string): string =>
  fromTime === toTime ? `ساعت ${fromTime}` : `${fromTime} تا ${toTime}`

// What happened, said the way a person would: «دریافت ۱٬۰۰۰ صورتحساب از API نسخه ۲».
export const getBatchTitle = (batch: Pick<DashboardActivityBatch, 'entryType' | 'invoices'>) => {
  const count = `${formatInvoiceCount(batch.invoices)} صورتحساب`
  const source = getInvoiceEntrySourceInfo(batch.entryType)

  switch (batch.entryType) {
    case 1:
    case 6:
      return `دریافت ${count} از ${source.label}`
    case 2:
      return `ورود ${count} از فایل اکسل`
    case 3:
      return `ورود ${count} از نرم‌افزار سپیدار`
    case 4:
      return `دریافت ${count} از نرم‌افزار حسابداری (همگام‌سازی)`
    case 5:
      return `صدور ${count} کارت‌خوان`
    case 11:
      return `صدور خودکار ${count}`
    case 12:
    case 13:
      return `ورود ${count} از ${source.label}`
    case 0:
    case 7:
    case 8:
    case 9:
    case 10:
      return `صدور ${count} با ${source.label}`
    default:
      return `ثبت ${count}`
  }
}

// Every link carries `counted`: the list then leaves out cancellation invoices and cancelled ones,
// as the dashboard's figures do, so it shows exactly the invoices a figure counted.

// The batch in the invoice list: its registration day, its channel and, when given, one status.
export const getBatchListUrl = (
  batch: Pick<DashboardActivityBatch, 'day' | 'entryType'>,
  listStatusKey?: string
) =>
  buildInvoiceListFilterUrl({
    saveFromDate: batch.day,
    saveToDate: batch.day,
    entryTypes:
      batch.entryType === undefined || batch.entryType === null ? undefined : [batch.entryType],
    sendStatus: listStatusKey,
    counted: true,
  })

export const getInvoiceUrl = (invoiceId: number) => buildInvoiceListTargetUrl(invoiceId)

// Every failing invoice of the window: the window's first day makes the list read every season.
export const getErroredListUrl = (windowFromDay?: string) =>
  buildInvoiceListFilterUrl({sendStatus: 'errored', saveFromDate: windowFromDay, counted: true})

export const getSendQueueListUrl = (listStatusKey: 'pending' | 'unknown') =>
  buildInvoiceListFilterUrl({sendStatus: listStatusKey, counted: true})

export const getProductListUrl = (
  product: Pick<DashboardProduct, 'stuffId' | 'internalCode' | 'name'>
) =>
  buildInvoiceListFilterUrl({
    productSearch: (product.stuffId || product.internalCode || product.name || '').trim(),
    counted: true,
  })

const OPERATION_TITLES: Record<string, string> = {
  excelImport: 'ورود صورتحساب از فایل اکسل',
  posInvoices: 'صدور صورتحساب تراکنش‌های کارت‌خوان',
  bulkSend: 'ارسال گروهی به سامانه مودیان',
  dateChange: 'تغییر گروهی تاریخ صورتحساب',
  bulkCancel: 'ابطال گروهی صورتحساب‌ها',
}

const ACCOUNTING_SYNC_TITLES: Record<number, string> = {
  1: 'ثبت صورتحساب‌های نرم‌افزار حسابداری',
  2: 'استعلام وضعیت صورتحساب‌های حسابداری',
  3: 'بازنشانی صورتحساب‌های حذف‌شده در حسابداری',
}

export const getOperationTitle = (operation: DashboardLiveOperation): string => {
  if (operation.kind === 'accountingSync') {
    const title =
      ACCOUNTING_SYNC_TITLES[Number(operation.subKind)] || 'همگام‌سازی با نرم‌افزار حسابداری'
    return operation.mode === 3 ? `${title} (خودکار)` : title
  }

  if (operation.kind === 'dateChange' && operation.targetDay) {
    return `${OPERATION_TITLES.dateChange} به ${operation.targetDay}`
  }

  return OPERATION_TITLES[operation.kind] || 'عملیات گروهی'
}

export const getOperationIcon = (operation: DashboardLiveOperation): string => {
  switch (operation.kind) {
    case 'excelImport':
      return 'la-file-excel'
    case 'posInvoices':
      return 'la-cash-register'
    case 'bulkSend':
      return 'la-paper-plane'
    case 'dateChange':
      return 'la-calendar-alt'
    case 'accountingSync':
      return 'la-sync'
    case 'bulkCancel':
      return 'la-ban'
    default:
      return 'la-tasks'
  }
}

const STATE_LABELS: Record<string, string> = {
  running: 'در حال انجام',
  preparing: 'در حال آماده‌سازی فایل',
  queued: 'در صف اجرا',
  waitingForAgent: 'در انتظار اتصال نرم‌افزار همگام‌سازی',
  stalled: 'متوقف‌شده؛ نیاز به بررسی',
}

export const getOperationStateLabel = (state: string) => STATE_LABELS[state] || 'در حال انجام'

// «۸۴ از ۱۲۰ صورتحساب (۷۰٪)، ۲ ناموفق»: the share belongs to the progress, never to the failures.
// A Persian comma, not a middle dot: beside Persian digits «·» reads as a zero («۰»).
export const getOperationProgressText = (operation: DashboardLiveOperation): string => {
  const parts: string[] = []
  const total = Number(operation.total || 0)
  const done = Number(operation.done || 0)
  if (total > 0 && done > 0) {
    const share =
      operation.progressPercent === null || operation.progressPercent === undefined
        ? ''
        : ` (${formatInvoiceCount(operation.progressPercent)}٪)`
    parts.push(`${formatInvoiceCount(done)} از ${formatInvoiceCount(total)} صورتحساب${share}`)
  } else if (total > 0) {
    parts.push(`${formatInvoiceCount(total)} صورتحساب`)
  } else if (done > 0) {
    parts.push(`${formatInvoiceCount(done)} صورتحساب انجام‌شده`)
  }

  if (operation.failed > 0) {
    parts.push(`${formatInvoiceCount(operation.failed)} ناموفق`)
  }

  return parts.join('، ')
}

// Where the operation's own page shows its rows, errors and retry.
export const getOperationUrl = (operation: DashboardLiveOperation): string | undefined => {
  if (operation.kind === 'accountingSync') return '/apps/accounting-sync/logs'
  if (operation.kind === 'bulkCancel') return '/apps/ebtal-management/ebtals'
  return operation.logId ? `/apps/log-management/logs/${operation.logId}` : undefined
}

export const getProductQuantityText = (product: DashboardProduct) => {
  const quantity = Number(product.quantity || 0)
  if (quantity <= 0) return ''
  const rounded = Math.round(quantity * 1000) / 1000
  return `${rounded.toLocaleString('en-US')}${product.unitName ? ` ${product.unitName}` : ''}`
}
